import { api, apiUrl } from './client';
import type { components } from './schema.generated';

export type Schemas = components['schemas'];
export type CampaignRole = Schemas['StructureCampaign']['role'];
export type StructureLevel = Schemas['DeliverySeries']['level'];

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((item) => {
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: unknown }).msg ?? '');
          }
          return String(item);
        })
        .filter(Boolean)
        .join(', ');
    }
    if ('message' in error) {
      const message = String((error as { message?: unknown }).message ?? '');
      if (message.trim()) return message;
    }
  }
  return fallback;
}

/** Loose user shape until the backend exposes a tighter response contract. */
export interface UserMe {
  id: number;
  email: string;
  display_name?: string | null;
  role?: 'admin' | 'manager' | 'cmo' | 'user' | null;
  global_role?: 'admin' | 'manager' | 'cmo' | 'user' | null;
  company_id?: number | null;
  company_name?: string | null;
  current_company_id?: number | null;
  companies?: Array<{ id: number; name: string; slug?: string }>;
  email_verified?: boolean;
  full_name?: string | null;
  job_title?: string | null;
  phone?: string | null;
  /** Relativa al backend (/api/users/{id}/avatar?v=...). Prefijar con apiUrl(). */
  avatar_url?: string | null;
  preferences?: Schemas['UserPreferences'] | null;
}

interface LoginResult {
  access_token?: string;
  refresh_token?: string;
  user?: UserMe;
  expires_in?: number;
}

export const authApi = {
  me: () => api.GET('/api/auth/me', {}),
  login: (body: Schemas['LoginRequest']) =>
    api.POST('/api/auth/login', { body }) as unknown as Promise<{
      data?: LoginResult;
      error?: { detail?: string };
    }>,
  register: (body: Schemas['RegisterRequest']) => api.POST('/api/auth/register', { body }),
  logout: () => api.POST('/api/auth/logout', {}),
  switchCompany: (body: Schemas['SwitchCompanyRequest']) =>
    api.POST('/api/auth/switch-company', { body }),
  refresh: (refresh_token: string) =>
    api.POST('/api/auth/refresh', { body: { refresh_token } as Schemas['RefreshTokenRequest'] }),
  forgotPassword: (body: Schemas['ForgotPasswordRequest']) =>
    api.POST('/api/auth/forgot-password', { body }),
  resetPassword: (body: Schemas['ResetPasswordRequest']) =>
    api.POST('/api/auth/reset-password', { body }),
  changePassword: (body: { current_password: string; new_password: string }) =>
    (
      api as unknown as {
        POST: (
          path: '/api/auth/change-password',
          opts: { body: { current_password: string; new_password: string } },
        ) => Promise<{ data?: { detail?: string }; error?: { detail?: string } }>;
      }
    ).POST('/api/auth/change-password', { body }),
  verifyEmail: (body: Schemas['VerifyEmailRequest']) =>
    api.POST('/api/auth/verify-email', { body }),
  resendVerification: (body: Schemas['ResendVerificationRequest']) =>
    api.POST('/api/auth/resend-verification', { body }),
  requestEmailChange: (body: Schemas['RequestEmailChangeRequest']) =>
    api.POST('/api/auth/request-email-change', { body }),
  confirmEmailChange: (body: Schemas['ConfirmEmailChangeRequest']) =>
    api.POST('/api/auth/confirm-email-change', { body }),
};

export const usersApi = {
  updateMe: (body: Schemas['UserProfileUpdateRequest']) => api.PATCH('/api/users/me', { body }),
  updateMyPreferences: (body: Schemas['UserPreferences']) =>
    api.PUT('/api/users/me/preferences', { body }),
  uploadMyAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.POST('/api/users/me/avatar', {
      body: form as unknown as Schemas['Body_upload_my_avatar_api_users_me_avatar_post'],
      bodySerializer: (b: unknown) => b as FormData,
    });
  },
  removeMyAvatar: () => api.DELETE('/api/users/me/avatar', {}),
  list: (opts: { with_details?: boolean; scope?: string; include_inactive?: boolean } = {}) =>
    api.GET('/api/users', { params: { query: opts } }),
  get: (user_id: number) => api.GET('/api/users/{user_id}', { params: { path: { user_id } } }),
  create: (body: Schemas['UserCreateRequest']) => api.POST('/api/users', { body }),
  update: (user_id: number, body: Schemas['UserUpdateRequest']) =>
    api.PATCH('/api/users/{user_id}', { params: { path: { user_id } }, body }),
  remove: (user_id: number) =>
    api.DELETE('/api/users/{user_id}', { params: { path: { user_id } } }),
};

export const companiesApi = {
  list: (opts: { scope?: string; include_inactive?: boolean } = {}) =>
    api.GET('/api/companies', { params: { query: opts } }),
  create: (body: Schemas['CompanyCreate']) => api.POST('/api/companies', { body }),
  update: (company_id: number, body: Schemas['CompanyUpdate']) =>
    api.PUT('/api/companies/{company_id}', { params: { path: { company_id } }, body }),
  remove: (company_id: number) =>
    api.DELETE('/api/companies/{company_id}', { params: { path: { company_id } } }),
};

export type CustomFieldEntity = 'campaign' | 'adset' | 'creative';

export interface MetaDetectedField {
  key: string;
  entity: CustomFieldEntity;
  label: string;
  field_type: 'text' | 'select' | 'number' | 'date' | 'bool' | 'url';
  classification: 'meta' | 'extra' | 'specific';
  options?: string[] | null;
  coverage_count: number;
  sample_values: string[];
  source_paths: string[];
  field_id: number;
  is_active: boolean;
  is_global: boolean;
}

export const customFieldsApi = {
  list: (entity: CustomFieldEntity, opts: { include_inactive?: boolean; scope?: string } = {}) =>
    api.GET('/api/custom-fields', {
      params: {
        query: {
          entity,
          include_inactive: opts.include_inactive,
          scope: opts.scope,
        },
      },
    }),
  create: (body: Schemas['CustomFieldCreate'], opts: { scope?: 'all' } = {}) =>
    api.POST('/api/custom-fields', {
      params: { query: opts.scope ? { scope: opts.scope } : {} },
      body,
    }),
  update: (field_id: number, body: Schemas['CustomFieldUpdate'], opts: { scope?: 'all' } = {}) =>
    api.PUT('/api/custom-fields/{field_id}', {
      params: {
        path: { field_id },
        query: opts.scope ? { scope: opts.scope } : {},
      },
      body,
    }),
  remove: (field_id: number, opts: { scope?: 'all'; hard?: boolean } = {}) =>
    api.DELETE('/api/custom-fields/{field_id}', {
      params: {
        path: { field_id },
        query: {
          ...(opts.scope ? { scope: opts.scope } : {}),
          ...(opts.hard ? { hard: true } : {}),
        },
      },
    }),
  metaDetected: (entity: CustomFieldEntity) =>
    api.GET('/api/custom-fields/meta-detected', {
      params: { query: { entity } },
    }) as unknown as Promise<{
      data?: { status: string; entity: CustomFieldEntity; items: MetaDetectedField[] };
      error?: unknown;
    }>,
  metaDetectedUse: (body: Schemas['MetaDetectedFieldUseRequest']) =>
    api.POST('/api/custom-fields/meta-detected/use', { body }),
};

export const namingApi = {
  // UTMs canónicas (página `/configuracion/reglas`).
  utmTemplate: () => api.GET('/api/naming/utm-template', {}),
  updateUtmTemplate: (body: Schemas['UtmTemplateUpdate']) =>
    api.PUT('/api/naming/utm-template', { body }),
  utmHealth: () => api.GET('/api/naming/utm-health', {}),
  utmHealthOrphans: (q: { page?: number; page_size?: number } = {}) =>
    api.GET('/api/naming/utm-health/orphans', { params: { query: q } }),

  // Endpoints legacy del JSON config — el editor de UI fue retirado en favor de UTMs canónicas.
  // Se mantienen exportados porque el flow `naming.publish_config → ensure_company_custom_fields`
  // (backend) los sigue usando para sembrar custom fields. No usar desde nueva UI.
  // @deprecated UI direct usage (ver `pages/configuracion/ReglasPage.tsx`).
  config: () => api.GET('/api/naming/config', {}),
  history: () => api.GET('/api/naming/history', {}),
  versioning: () => api.GET('/api/naming/config/versioning', {}),
  updateConfig: (body: Schemas['ConfigUpdate']) => api.PUT('/api/naming/config', { body }),
  publishConfig: () => api.POST('/api/naming/config/publish', {}),
  rollbackConfig: (body: Schemas['ConfigRollbackRequest']) =>
    api.POST('/api/naming/config/rollback', { body }),
};

export const aiApi = {
  // Track 1 naming suggestion: deterministic → cache → LLM fallback.
  namingSuggest: (body: Schemas['NamingAiSuggestionRequest']) =>
    api.POST('/api/ai/naming/suggest', { body }),
  orphans: () => api.GET('/api/ai/orphans', {}),
  orphanSuggest: (orphanId: number) =>
    api.POST('/api/ai/orphans/{orphan_id}/suggest', {
      params: { path: { orphan_id: orphanId } },
    }),
  orphanAccept: (orphanId: number, body: Schemas['AcceptOrphanRequest']) =>
    api.POST('/api/ai/orphans/{orphan_id}/accept', {
      params: { path: { orphan_id: orphanId } },
      body,
    }),
  orphanDismiss: (orphanId: number, body: Schemas['DismissOrphanRequest']) =>
    api.POST('/api/ai/orphans/{orphan_id}/dismiss', {
      params: { path: { orphan_id: orphanId } },
      body,
    }),
  dedupeProposals: () => api.GET('/api/ai/dedupe/proposals', {}),
  decideDedupeProposal: (proposalId: number, decision: 'merge' | 'keep_separate') =>
    api.POST('/api/ai/dedupe/proposals/{proposal_id}/decide', {
      params: { path: { proposal_id: proposalId } },
      body: { decision },
    }),
  trackingProposals: () => api.GET('/api/ai/tracking/proposals', {}),
  acceptTrackingProposal: (decisionId: number) =>
    api.POST('/api/ai/tracking/proposals/{decision_id}/accept', {
      params: { path: { decision_id: decisionId } },
    }),
  dismissTrackingProposal: (decisionId: number) =>
    api.POST('/api/ai/tracking/proposals/{decision_id}/dismiss', {
      params: { path: { decision_id: decisionId } },
    }),
};

export const integrationsApi = {
  overview: () => api.GET('/api/leads/integrations/overview', {}),
  meta: () => api.GET('/api/leads/integrations/meta', {}),
  updateMeta: (body: Schemas['MetaIntegrationConfigUpdate']) =>
    api.PUT('/api/leads/integrations/meta', { body }),
  testMetaConnection: () => api.POST('/api/leads/integrations/meta/test-connection', {}),
  metaOauthAuthorize: () => api.GET('/api/leads/integrations/meta/oauth/authorize', {}),
  googleAnalyticsOauthAuthorize: () =>
    api.GET('/api/leads/integrations/google-analytics/oauth/authorize', {}),
  metaHealth: () => api.GET('/api/leads/integrations/meta/health', {}),
  shopify: () => api.GET('/api/leads/integrations/shopify', {}),
  updateShopify: (body: Schemas['ShopifyIntegrationConfigUpdate']) =>
    api.PUT('/api/leads/integrations/shopify', { body }),
  analytics: () => api.GET('/api/leads/integrations/google-analytics', {}),
  updateAnalytics: (body: Schemas['GoogleAnalyticsIntegrationConfigUpdate']) =>
    api.PUT('/api/leads/integrations/google-analytics', { body }),
  klaviyo: () => api.GET('/api/leads/integrations/klaviyo', {}),
  updateKlaviyo: (body: Schemas['KlaviyoIntegrationConfigUpdate']) =>
    api.PUT('/api/leads/integrations/klaviyo', { body }),
  clarity: () => api.GET('/api/leads/integrations/clarity', {}),
  updateClarity: (body: Schemas['ClarityIntegrationConfigUpdate']) =>
    api.PUT('/api/leads/integrations/clarity', { body }),
  holded: () => api.GET('/api/leads/integrations/holded', {}),
  updateHolded: (body: Schemas['HoldedIntegrationConfigUpdate']) =>
    api.PUT('/api/leads/integrations/holded', { body }),
  metaSyncOverview: () => api.GET('/api/meta/sync/overview', {}),
  shopifySyncOverview: () => api.GET('/api/shopify/sync/overview', {}),
  linkShopifyApp: (shop_domain: string) =>
    api.POST('/api/shopify/app/link', { body: { shop_domain } }),
  shopifyWebhookInboxOverview: () => api.GET('/api/shopify/webhooks/inbox/overview', {}),
  retryShopifyWebhookInboxRow: (inbox_id: number) =>
    api.POST('/api/shopify/webhooks/inbox/{inbox_id}/retry', {
      params: { path: { inbox_id } },
    }),
  // Sync unitario por proveedor. Desde que GA4, Klaviyo y Clarity tienen fase
  // en el orquestador, el cron horario ya los sincroniza solo. Estos endpoints
  // siguen siendo los del boton manual: corren inline —cuando responden, ya
  // termino— y se saltan las cadencias del cron, asi que son la via para
  // forzar un sync ahora (en Clarity, el unico camino si ya corrio hoy).
  triggerGoogleAnalyticsSync: (body: Schemas['LeadSyncRequest']) =>
    api.POST('/api/leads/sync/google-analytics', { body }),
  triggerKlaviyoSync: (body: Schemas['LeadSyncRequest']) =>
    api.POST('/api/leads/sync/klaviyo', { body }),
  triggerClaritySync: (body: Schemas['LeadSyncRequest']) =>
    api.POST('/api/leads/sync/clarity', { body }),
  triggerHoldedSync: (body: Schemas['LeadSyncRequest']) =>
    api.POST('/api/leads/sync/holded', { body }),
  diagnostics: (provider: 'meta' | 'shopify', force = false) =>
    api.GET('/api/integrations/{provider}/diagnostics', {
      params: { path: { provider }, query: { force } },
    }),
};

export const notificationsApi = {
  list: () => api.GET('/api/notifications', {}),
  markRead: (notification_id: string | number) =>
    api.POST('/api/notifications/{notification_id}/read', {
      params: { path: { notification_id: Number(notification_id) } },
    }),
  markAllRead: () => api.POST('/api/notifications/read-all', {}),
};

export const tracePilotApi = {
  health: (params: { from?: string; to?: string } = {}) =>
    api.GET('/api/trace-pilot/health', { params: { query: params } }),
  listOrders: (
    params: {
      confidence?: 'hard' | 'soft' | 'unknown';
      from?: string;
      to?: string;
      creative_id?: number;
      failure_code?: string;
      traffic_source?: string;
      attributed?: boolean;
      cursor?: string;
      limit?: number;
    } = {},
  ) => api.GET('/api/trace-pilot/orders', { params: { query: params } }),
  creatives: (params: { from?: string; to?: string } = {}) =>
    api.GET('/api/trace-pilot/creatives', { params: { query: params } }),
  getOrderDetail: (order_id: number) =>
    api.GET('/api/trace-pilot/orders/{order_id}', {
      params: { path: { order_id } },
    }),
  diagnosisSummary: (params: { from?: string; to?: string } = {}) =>
    api.GET('/api/trace-pilot/diagnosis/summary', {
      params: { query: params },
    }),
  adSummary: (ad_id: number, params: { from?: string; to?: string } = {}) =>
    api.GET('/api/trace-pilot/ads/{ad_id}/orders', {
      params: { path: { ad_id }, query: params },
    }),
  urlTagsAudit: () => api.GET('/api/trace-pilot/url-tags-audit', {}),
  runUrlTagsAudit: () => api.POST('/api/trace-pilot/url-tags-audit/run', {}),
  fixUrlTags: (account_id: string) =>
    api.POST('/api/trace-pilot/url-tags-audit/fix/{account_id}', {
      params: { path: { account_id } },
    }),
  recompute: (body: {
    order_id?: number;
    from_date?: string;
    to_date?: string;
    include_unknown?: boolean;
  }) => api.POST('/api/trace-pilot/recompute', { body }),
};

export const jobsApi = {
  get: (job_id: string) => api.GET('/api/jobs/{job_id}', { params: { path: { job_id } } }),
};

export interface LeadsListQuery {
  q?: string;
  status?: string;
  client_id?: number;
  include_archived?: boolean;
  limit?: number;
  offset?: number;
}

export const leadsApi = {
  list: (q: LeadsListQuery = {}) => api.GET('/api/leads', { params: { query: q } }),
  create: (body: Schemas['LeadCreate']) => api.POST('/api/leads', { body }),
  update: (lead_id: number, body: Schemas['LeadUpdate']) =>
    api.PUT('/api/leads/{lead_id}', { params: { path: { lead_id } }, body }),
  remove: (lead_id: number) =>
    api.DELETE('/api/leads/{lead_id}', { params: { path: { lead_id } } }),
  conversion: (lead_id: number) =>
    api.GET('/api/leads/{lead_id}/conversion', { params: { path: { lead_id } } }),
};

export interface LibraryListQuery {
  q?: string;
  status?: string;
  source?: string;
  platform?: string;
  objective?: string;
  format?: string;
  /** JSON-encoded `{ key: value }` for filtering by custom-field overrides in `meta`. */
  meta_filter?: string;
  campaign_id?: number;
  adset_id?: number;
  limit?: number;
  offset?: number;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  include_archived?: boolean;
  scope?: string;
}

export const libraryApi = {
  campaigns: (q: LibraryListQuery = {}) =>
    api.GET('/api/library/campaigns', { params: { query: q } }),
  adsets: (q: LibraryListQuery = {}) => api.GET('/api/library/adsets', { params: { query: q } }),
  creatives: (q: LibraryListQuery = {}) =>
    api.GET('/api/library/creatives', { params: { query: q } }),
  updateCampaign: (campaign_id: number, body: Schemas['CampaignUpdate']) =>
    api.PUT('/api/library/campaigns/{campaign_id}', {
      params: { path: { campaign_id } },
      body,
    }),
  updateAdset: (adset_id: number, body: Schemas['AdSetUpdate']) =>
    api.PUT('/api/library/adsets/{adset_id}', {
      params: { path: { adset_id } },
      body,
    }),
  updateCreative: (creative_id: number, body: Schemas['CreativeUpdate']) =>
    api.PUT('/api/library/creatives/{creative_id}', {
      params: { path: { creative_id } },
      body,
    }),
  // `null` devuelve la campaña al rol propuesto por su nombre.
  setCampaignRole: (campaign_id: number, method_role: CampaignRole | null) =>
    api.PUT('/api/library/campaigns/{campaign_id}/method-role', {
      params: { path: { campaign_id } },
      body: { method_role },
    }),
  resetCampaignToMeta: (campaign_id: number) =>
    api.POST('/api/library/campaigns/{campaign_id}/reset-meta', {
      params: { path: { campaign_id } },
    }),
  resetAdsetToMeta: (adset_id: number) =>
    api.POST('/api/library/adsets/{adset_id}/reset-meta', {
      params: { path: { adset_id } },
    }),
  resetCreativeToMeta: (creative_id: number) =>
    api.POST('/api/library/creatives/{creative_id}/reset-meta', {
      params: { path: { creative_id } },
    }),
};

export const businessApi = {
  profile: (q: { profile_id?: number } = {}) =>
    api.GET('/api/business/profile', { params: { query: q } }),
  updateProfile: (body: Schemas['BusinessProfileUpdate']) =>
    api.PUT('/api/business/profile', { body }),
  // Profile versioning — fase 3 (escenarios sandbox).
  listProfiles: (q: { kind?: 'production' | 'scenario' } = {}) =>
    api.GET('/api/business/profiles', { params: { query: q } }),
  createScenario: (body: Schemas['BusinessProfileScenarioCreate']) =>
    api.POST('/api/business/profiles', { body }),
  updateScenario: (profile_id: number, body: Schemas['BusinessProfileScenarioUpdate']) =>
    api.PUT('/api/business/profiles/{profile_id}', {
      params: { path: { profile_id } },
      body,
    }),
  deleteScenario: (profile_id: number) =>
    api.DELETE('/api/business/profiles/{profile_id}', {
      params: { path: { profile_id } },
    }),
  promoteScenario: (profile_id: number) =>
    api.POST('/api/business/profiles/{profile_id}/promote', {
      params: { path: { profile_id } },
    }),
  setScenarioActive: (profile_id: number, is_active: boolean) =>
    api.PATCH('/api/business/profiles/{profile_id}/active', {
      params: { path: { profile_id } },
      body: { is_active },
    }),
  strategyVerdict: (profile_id: number) =>
    api.GET('/api/business/profiles/{profile_id}/verdict', {
      params: { path: { profile_id } },
    }),
  viability: () => api.GET('/api/business/profile/viability', {}),
  override: (body: Schemas['ViabilityOverrideRequest']) =>
    api.POST('/api/business/profile/viability/override', { body }),
  runway: () => api.GET('/api/business/profile/runway', {}),
  funnelArchitecture: (q: { business_type?: string } = {}) =>
    api.GET('/api/business/funnel-architecture', { params: { query: q } }),
  // Codegen pendiente: `/funnel-metrics` se añadió al backend pero
  // `schema.generated.ts` aún no regenerado. Cast escape until next codegen run.
  funnelMetrics: (q: { start_date?: string; end_date?: string; business_type?: string } = {}) =>
    (
      api.GET as unknown as (
        path: string,
        opts: { params: { query: typeof q } },
      ) => Promise<{ data?: unknown; error?: unknown }>
    )('/api/business/funnel-metrics', { params: { query: q } }),
  // Con los archivados: una variante vendida sigue costeándose aunque ya no
  // esté activa, y hay que poder encontrarla y corregir su coste (ficha 04).
  products: (q: { include_inactive?: boolean } = { include_inactive: true }) =>
    api.GET('/api/business/products', { params: { query: q } }),
  approvePendingProductCost: (product_id: number) =>
    api.POST('/api/business/products/{product_id}/costs/pending/approve', {
      params: { path: { product_id } },
    }),
  discardPendingProductCost: (product_id: number) =>
    api.DELETE('/api/business/products/{product_id}/costs/pending', {
      params: { path: { product_id } },
    }),
  updateProduct: (product_id: number, body: Schemas['BusinessProductUpdate']) =>
    api.PUT('/api/business/products/{product_id}', {
      params: { path: { product_id } },
      body,
    }),
  importShopifyProducts: (q: { limit?: number } = {}) =>
    api.POST('/api/business/products/import/shopify', { params: { query: q } }),
  importProductsExcel: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.POST('/api/business/products/import/excel', {
      body: form as unknown as Schemas['Body_import_products_from_excel_api_business_products_import_excel_post'],
      bodySerializer: (b: unknown) => b as FormData,
    });
  },
  productCostHistory: (product_id: number) =>
    api.GET('/api/business/products/{product_id}/cost-history', {
      params: { path: { product_id } },
    }),
  productProfitability: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/business/product-profitability', { params: { query: q } }),
  customerCohorts: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/business/customer-cohorts', { params: { query: q } }),
};

/** Capa canonica de metricas (fase 4 de `capa-de-metricas.md`). Aun no la
 * pinta ninguna pantalla del cliente. */
export const metricsApi = {
  summary: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/summary', { params: { query: q } }),
  shadow: (q: { screen?: 'inicio'; from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/shadow', { params: { query: q } }),
  customers: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/customers', { params: { query: q } }),
  /** Cohortes por mes de primera compra con ventanas comunes D30/D60/D90. */
  customerCohorts: (q: { months?: number } = {}) =>
    api.GET('/api/metrics/customers/cohorts', { params: { query: q } }),
  /** Objetivos económicos, viabilidad separada y objetivo único de CPBI (fichas 03, 07 y 08). */
  economicObjectives: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/economic-objectives', { params: { query: q } }),
  /** Cuándo cambiaron por última vez los datos de la empresa (refresco automático). */
  version: () => api.GET('/api/metrics/version', {}),
  /** Fichas 19 y 20: poblaciones, bases fiscales, ticket y reembolsos. */
  orderBases: (q: { from_date?: string; to_date?: string; currency?: string } = {}) =>
    api.GET('/api/metrics/order-bases', { params: { query: q } }),
  /** Ficha 29: descuentos y regalos por oferta; por código como asociación. */
  discounts: (q: { from_date?: string; to_date?: string; currency?: string } = {}) =>
    api.GET('/api/metrics/discounts', { params: { query: q } }),
  /** Ficha 21: estado de cada proceso y de cada métrica para un rango. */
  health: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/health', { params: { query: q } }),
  /** Embudo ecommerce de /web con su granularidad declarada (ficha 17). */
  webFunnel: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/web-funnel', { params: { query: q } }),
  /** Jerarquía de evidencia de atribución y su versión (ficha 16). */
  attributionEvidence: () => api.GET('/api/metrics/attribution-evidence', {}),
  /** Contrato `cdp.metrics.export` (ficha 28) en JSON. */
  exportJson: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/export', { params: { query: { ...q, format: 'json' } } }),
  /** El mismo contrato en CSV, como texto. */
  exportCsv: (q: { from_date?: string; to_date?: string } = {}) =>
    api.GET('/api/metrics/export', {
      params: { query: { ...q, format: 'csv' } },
      parseAs: 'text',
    }),
  createSnapshot: (body: Schemas['MetricSnapshotCreate']) =>
    api.POST('/api/metrics/snapshots', { body }),
  snapshots: (limit = 50) => api.GET('/api/metrics/snapshots', { params: { query: { limit } } }),
};

// Objetivos por empresa (fichas 25 y 26): versiones de solo inserción.
export const objectivesApi = {
  list: (q: { include_archived?: boolean; scope?: 'production' | 'scenario' } = {}) =>
    api.GET('/api/objectives', { params: { query: q } }),
  templates: () => api.GET('/api/objectives/templates', {}),
  applyTemplate: (template: 'b2c' | 'b2b') =>
    api.POST('/api/objectives/templates/apply', { body: { template } }),
  create: (body: Schemas['ObjectiveCreate']) => api.POST('/api/objectives', { body }),
  createVersion: (objective_key: string, body: Schemas['ObjectiveVersionCreate']) =>
    api.POST('/api/objectives/{objective_key}/versions', {
      params: { path: { objective_key } },
      body,
    }),
  history: (objective_key: string) =>
    api.GET('/api/objectives/{objective_key}/history', { params: { path: { objective_key } } }),
  evaluation: (q: { from_date: string; to_date: string }) =>
    api.GET('/api/objectives/evaluation', { params: { query: q } }),
};

// Cartera de la agencia (ficha 25): solo admin global.
export const agencyApi = {
  portfolio: () => api.GET('/api/agency/portfolio', {}),
  previewImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.POST('/api/agency/import/preview', {
      body: form as unknown as Schemas['Body_preview_import_api_agency_import_preview_post'],
      bodySerializer: (b: unknown) => b as FormData,
    });
  },
  applyImport: (file: File, checksum: string, reason: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('checksum', checksum);
    form.append('reason', reason);
    return api.POST('/api/agency/import/apply', {
      body: form as unknown as Schemas['Body_apply_import_api_agency_import_apply_post'],
      bodySerializer: (b: unknown) => b as FormData,
    });
  },
};

// Contexto operativo del escalado (ficha 27).
export const operationsApi = {
  context: () => api.GET('/api/operations/context', {}),
};

export const datasetsApi = {
  get: (q: {
    dataset:
      | 'meta_creative_daily'
      | 'shopify_orders'
      | 'ga4_sessions'
      | 'ga4_ecommerce'
      | 'klaviyo_campaigns'
      | 'klaviyo_flows'
      | 'klaviyo_forms'
      | 'clarity_daily'
      | 'capi_events'
      | 'capi_attribution'
      | 'capi_health';
    from_date: string;
    to_date: string;
    limit?: number;
  }) => api.GET('/api/datasets', { params: { query: q } }),
};

export type KlaviyoOverview = Schemas['KlaviyoOverviewResponse'];
export type KlaviyoFlowFunction = NonNullable<Schemas['KlaviyoFlowFunctionUpdate']['function']>;

/** Vista de Klaviyo por capas y función de negocio de los flujos (fichas 13 y 14). */
export const klaviyoApi = {
  overview: (from_date: string, to_date: string) =>
    api.GET('/api/klaviyo/overview', { params: { query: { from_date, to_date } } }),
  // `null` borra la asignación: el flujo vuelve a «sin clasificar».
  setFlowFunction: (flow_id: string, fn: KlaviyoFlowFunction | null) =>
    api.PUT('/api/klaviyo/flows/{flow_id}/function', {
      params: { path: { flow_id } },
      body: { function: fn },
    }),
};

export const cdpApi = {
  runway: () => api.GET('/api/cdp/runway', {}),
  attributions: (limit?: number) =>
    api.GET('/api/cdp/attributions', limit != null ? { params: { query: { limit } } } : {}),
  costsMarketing: () => api.GET('/api/cdp/costs/marketing', {}),
  upsertMarketingCost: (body: Schemas['MarketingDepartmentCostPayload']) =>
    api.PUT('/api/cdp/costs/marketing', { body }),
  // Gap 3.6 pieza 1 — marketing estructural NO-Ads.
  listMarketingStructural: (q: { month_from?: string; month_to?: string } = {}) =>
    api.GET('/api/cdp/marketing-structural-costs', { params: { query: q } }),
  createMarketingStructural: (body: Schemas['MarketingStructuralCostCreate']) =>
    api.POST('/api/cdp/marketing-structural-costs', { body }),
  updateMarketingStructural: (row_id: number, body: Schemas['MarketingStructuralCostUpdate']) =>
    api.PUT('/api/cdp/marketing-structural-costs/{row_id}', {
      params: { path: { row_id } },
      body,
    }),
  deleteMarketingStructural: (row_id: number) =>
    api.DELETE('/api/cdp/marketing-structural-costs/{row_id}', {
      params: { path: { row_id } },
    }),
  // Defaults mensuales (Quique 2026-06-05): personalizar / revertir un mes.
  materializeMarketingStructuralMonth: (body: Schemas['MarketingStructuralMonthAction']) =>
    api.POST('/api/cdp/marketing-structural-costs/materialize-month', { body }),
  revertMarketingStructuralMonth: (body: Schemas['MarketingStructuralMonthAction']) =>
    api.POST('/api/cdp/marketing-structural-costs/revert-month', { body }),
};

export const marketingReportsApi = {
  history: () => api.GET('/api/marketing-reports'),
  detail: (id: number) =>
    api.GET('/api/marketing-reports/{report_id}', { params: { path: { report_id: id } } }),
  generate: (body: Schemas['GenerateMarketingReport']) =>
    api.POST('/api/marketing-reports', { body }),
  review: (id: number, body: Schemas['ReportReviewRequest']) =>
    api.POST('/api/marketing-reports/{report_id}/reviews', {
      params: { path: { report_id: id } },
      body,
    }),
};

/** Revisión semanal: portada e hitos con propietario (ficha 24). */
export const weeklyReviewApi = {
  get: (cutoff?: string) =>
    api.GET('/api/weekly-review', { params: { query: cutoff ? { cutoff } : {} } }),
  createMilestone: (body: Schemas['MilestoneCreate']) =>
    api.POST('/api/weekly-review/milestones', { body }),
  updateMilestone: (id: number, body: Schemas['MilestoneUpdate']) =>
    api.PUT('/api/weekly-review/milestones/{milestone_id}', {
      params: { path: { milestone_id: id } },
      body,
    }),
  confirmOwner: (id: number) =>
    api.POST('/api/weekly-review/milestones/{milestone_id}/confirm-owner', {
      params: { path: { milestone_id: id } },
    }),
};

/** Registro de pruebas del laboratorio creativo (ficha 23). */
export const creativeLabApi = {
  list: () => api.GET('/api/creative-lab/tests'),
  create: (body: Schemas['CreativeLabTestCreate']) => api.POST('/api/creative-lab/tests', { body }),
  update: (id: number, body: Schemas['CreativeLabTestUpdate']) =>
    api.PUT('/api/creative-lab/tests/{test_id}', { params: { path: { test_id: id } }, body }),
  verifyAudience: (id: number, evidence: string) =>
    api.POST('/api/creative-lab/tests/{test_id}/audience-verification', {
      params: { path: { test_id: id } },
      body: { evidence },
    }),
  clearAudience: (id: number) =>
    api.DELETE('/api/creative-lab/tests/{test_id}/audience-verification', {
      params: { path: { test_id: id } },
    }),
};

export const assistantApi = {
  conversations: () => api.GET('/api/assistant/conversations'),
  conversation: (id: number) =>
    api.GET('/api/assistant/conversations/{conversation_id}', {
      params: { path: { conversation_id: id } },
    }),
  send: (body: Schemas['AssistantSendMessage']) => api.POST('/api/assistant/messages', { body }),
  remove: (id: number) =>
    api.DELETE('/api/assistant/conversations/{conversation_id}', {
      params: { path: { conversation_id: id } },
    }),
};

export const aiQualityReviewApi = {
  get: () => api.GET('/api/ai-quality-review'),
  save: (questionId: string, body: Schemas['SaveReviewAnswer']) =>
    api.PUT('/api/ai-quality-review/answers/{question_id}', {
      params: { path: { question_id: questionId } },
      body,
    }),
};

export const reportingApi = {
  qqOverview: (q: { start_date?: string; end_date?: string; top_creatives_limit?: number } = {}) =>
    api.GET('/api/reporting/qq/overview', { params: { query: q } }),
  coreMethod: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/creatives/core-method', { params: { query: q } }),
  coreEconomic: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/creatives/core-economic', { params: { query: q } }),
  // Plan 2026-05-18 Fase 10 (transcript líneas 211-212): adquisición
  // histórica acumulada por creativa, sin filtro de rango.
  creativeHistoricalAcquisition: () =>
    api.GET('/api/reporting/creatives/historical-acquisition', {}),
  // Extensión Fase 10: cobertura de identificadores Meta (fbp/fbc/fbclid/utm)
  // en las orders Shopify.
  trackingCoverageMetaSignals: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/tracking-coverage/meta-signals', { params: { query: q } }),
  enrichOrdersFromSessions: (q: { include_heuristic_match?: boolean } = {}) =>
    api.POST('/api/reporting/tracking-coverage/enrich-from-sessions', {
      params: { query: q },
    }),
  // Backfill auxiliar: extrae browser_ip/user_agent del raw_payload
  // existente y los persiste en columnas tipadas. Habilita el cruce
  // heurístico IP+UA en `enrichOrdersFromSessions`.
  backfillBrowserIp: () => api.POST('/api/reporting/tracking-coverage/backfill-browser-ip', {}),
  funnel: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/creatives/funnel', { params: { query: q } }),
  hooksLeaderboard: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/creatives/hooks-leaderboard', { params: { query: q } }),
  hooksHeatmap: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/creatives/hooks-heatmap', { params: { query: q } }),
  // Gap 3.9 — agregación por tag (no por creative). Complementa el
  // leaderboard per-creative existente.
  hooksByTag: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/hooks', { params: { query: q } }),
  // Gap 3.6 piezas 2-3 — P&L por oferta + reparto estructural.
  offersPnl: (q: { start_date?: string; end_date?: string; include_inactive?: boolean } = {}) =>
    api.GET('/api/reporting/offers/pnl', { params: { query: q } }),
  offersPnlFull: (q: { start_date?: string; end_date?: string; include_inactive?: boolean } = {}) =>
    api.GET('/api/reporting/offers/pnl-full', { params: { query: q } }),
  creativeTimeseries: (creative_id: number, q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/creatives/{creative_id}/timeseries', {
      params: { path: { creative_id }, query: q },
    }),
  // Campaña → conjunto → anuncio con cifras QQ por nivel. `role=testing` es el
  // Laboratorio creativo.
  structure: (q: { start_date?: string; end_date?: string; role?: CampaignRole } = {}) =>
    api.GET('/api/reporting/structure', { params: { query: q } }),
  // Evolución diaria de entrega Meta de un anuncio, conjunto o campaña.
  structureSeries: (q: {
    level: StructureLevel;
    entity_id: number;
    start_date?: string;
    end_date?: string;
  }) => api.GET('/api/reporting/structure/series', { params: { query: q } }),
  directorsControl: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/directors-control', { params: { query: q } }),
  productMix: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/product-mix', { params: { query: q } }),
  trackingHealth: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/tracking-health', { params: { query: q } }),
  /** Población de cada bloque de /atribucion/salud y el puente entre ellos (ficha 15). */
  trackingPopulation: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/tracking-health/population', { params: { query: q } }),
};

export interface AuditListQuery {
  action_prefix?: string;
  actor_id?: number;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export const auditApi = {
  list: (q: AuditListQuery = {}) => api.GET('/api/audit/events', { params: { query: q } }),
};

// Cupones internos del tenant — los códigos activos se clasifican como
// `direct_internal` en lugar de `direct` genérico. Sin alta manual: la única
// vía de alta es shopifyDiscountsApi.setInternal (toggle sobre el espejo).
export const internalCouponsApi = {
  list: (include_inactive: boolean = false) =>
    api.GET('/api/internal-coupons', { params: { query: { include_inactive } } }),
  deactivate: (coupon_id: number) =>
    api.DELETE('/api/internal-coupons/{coupon_id}', {
      params: { path: { coupon_id } },
    }),
};

// Espejo de discount codes del Admin de Shopify. El toggle `setInternal`
// mantiene company_internal_coupons en linea (upsert / soft-delete).
// `create` da de alta EN Shopify (discountCodeBasicCreate) — no hay cupones
// locales: si Shopify rechaza, no se persiste nada.
export const shopifyDiscountsApi = {
  list: () => api.GET('/api/shopify/discounts', {}),
  create: (body: {
    code: string;
    title?: string | null;
    discount_type: 'percentage' | 'fixed_amount';
    value: number;
  }) => api.POST('/api/shopify/discounts', { body }),
  // Barrido completo sincrono — el universo de codigos por shop es pequeno.
  sync: () => api.POST('/api/shopify/discounts/sync', {}),
  setInternal: (discount_id: number, is_internal: boolean) =>
    api.PATCH('/api/shopify/discounts/{discount_id}/internal', {
      params: { path: { discount_id } },
      body: { is_internal },
    }),
};

export const sessionsApi = {
  list: () => api.GET('/api/auth/sessions', {}),
};

export const syncApi = {
  state: () => api.GET('/api/sync/state', {}),
  // ¿Meta o Shopify han sincronizado con éxito alguna vez? Señal canónica del
  // gate de onboarding/reporting (lee `sync_state`, no runs por proveedor).
  readiness: () => api.GET('/api/sync/readiness', {}),
  // Body opcional: `{ providers: ['meta'] }` acota la pasada a un proveedor
  // (sync unitario). Sin body → ambos providers habilitados.
  refreshNow: (body?: Schemas['SyncRefreshRequest']) =>
    api.POST('/api/sync/refresh-now', { body: body ?? {} }),
};

export const capiAdminApi = {
  getConfig: () => api.GET('/api/attribution/capi-config', {}),
  updateConfig: (body: Schemas['CapiConfigUpdateRequest']) =>
    api.PATCH('/api/attribution/capi-config', { body }),
  flush: () => api.POST('/api/attribution/capi-config/flush', {}),
  listEvents: (q: { limit?: number; status?: string } = {}) =>
    api.GET('/api/attribution/capi-events', { params: { query: q } }),
  stats: () => api.GET('/api/attribution/capi-stats', {}),
};

export const attributionApi = {
  diagnose: (orderId: number) =>
    api.GET('/api/attribution/diagnose/{order_id}', {
      params: { path: { order_id: orderId } },
    }),
  diagnoseJourney: (orderId: number, q: { limit?: number } = {}) =>
    api.GET('/api/attribution/diagnose/{order_id}/journey', {
      params: { path: { order_id: orderId }, query: q },
    }),
  trackingHealthSeries: (q: { weeks?: number } = {}) =>
    api.GET('/api/reporting/tracking-health/series', { params: { query: q } }),
  backfillRescueStats: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/attribution/backfill-rescue-stats', { params: { query: q } }),
  runBackfillRescue: () => api.POST('/api/attribution/backfill-rescue/run', {}),
  trafficSourceBreakdown: (q: { start_date?: string; end_date?: string } = {}) =>
    api.GET('/api/reporting/traffic-source-breakdown', { params: { query: q } }),
  missingSignalsSummary: (q: { start_date?: string; end_date?: string; limit?: number } = {}) =>
    api.GET('/api/attribution/missing-signals-summary', { params: { query: q } }),
  capiEmqTrend: (q: { weeks?: number } = {}) =>
    api.GET('/api/attribution/capi-emq-trend', { params: { query: q } }),
  reconciliation: (q: { start_date: string; end_date: string }) =>
    api.GET('/api/attribution/reconciliation', { params: { query: q } }),
  reconciliationPerAd: (q: { start_date: string; end_date: string; min_meta_purchases?: number }) =>
    api.GET('/api/attribution/reconciliation/per-ad', { params: { query: q } }),
};

export interface ShopifyOrdersListQuery {
  overview_scope?: boolean;
  commercial_only?: boolean;
  currency?: string;
  product_key?: string;
  paired_product_key?: string;
  customer_key?: string;
  customer_segment?: 'first_seen' | 'returning' | 'unknown';
  traffic_source?: string;
  attention?: 'pending' | 'unfulfilled' | 'partial' | 'cancelled' | 'refunded';
  discount_code?: string;
  basket_size?: '0' | '1' | '2' | '3+';
  ticket_band?: '0-25' | '25-50' | '50-100' | '100+';
  q?: string;
  date_from?: string;
  date_to?: string;
  financial_status?: string;
  fulfillment_status?: string;
  tracking_status?: string;
  sort_by?: 'placed_at' | 'total_amount' | 'order_number' | 'customer_email' | 'financial_status';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export const shopifyOrdersApi = {
  opportunities: (q: {
    date_from: string;
    date_to: string;
    reference_from: string;
    reference_to: string;
    currency?: string;
    selected_key?: string;
  }) => api.GET('/api/shopify/orders/opportunities', { params: { query: q } }),
  saveOpportunityRule: (body: Schemas['OpportunityRuleInput'], id?: number) =>
    id == null
      ? api.POST('/api/shopify/orders/opportunity-rules', { body })
      : api.PUT('/api/shopify/orders/opportunity-rules/{rule_id}', {
          params: { path: { rule_id: id } },
          body,
        }),
  overview: (q: {
    date_from: string;
    date_to: string;
    reference_from?: string;
    reference_to?: string;
    currency?: string;
  }) => api.GET('/api/shopify/orders/overview', { params: { query: q } }),
  list: (q: ShopifyOrdersListQuery = {}) =>
    api.GET('/api/shopify/orders', { params: { query: q } }),
  get: (orderId: number) =>
    api.GET('/api/shopify/orders/{order_id}', {
      params: { path: { order_id: orderId } },
    }),
};

export interface ShopifyCustomersListQuery {
  q?: string;
  date_from?: string;
  date_to?: string;
  sort_by?:
    | 'lifetime_revenue'
    | 'orders_count'
    | 'avg_order_value'
    | 'avg_recurrence_days'
    | 'last_order_at'
    | 'customer_email';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export const shopifyCustomersApi = {
  list: (q: ShopifyCustomersListQuery = {}) =>
    api.GET('/api/shopify/customers', { params: { query: q } }),
};

export interface MetaTransactionsListQuery {
  q?: string;
  date_from?: string;
  date_to?: string;
  sort_by?:
    'metric_date' | 'campaign_name' | 'spend' | 'revenue' | 'purchases' | 'impressions' | 'clicks';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export const metaTransactionsApi = {
  list: (q: MetaTransactionsListQuery = {}) =>
    api.GET('/api/meta/transactions', { params: { query: q } }),
};

export const recommendedActionsApi = {
  list: (
    params: {
      status?: 'open' | 'dismissed' | 'auto_resolved' | 'manually_resolved' | null;
      kind?: 'pause_kill_red' | null;
      page?: number;
      page_size?: number;
    } = {},
  ) => api.GET('/api/recommended-actions', { params: { query: params } }),
  summary: () => api.GET('/api/recommended-actions/summary', {}),
  dismiss: (action_id: number) =>
    api.POST('/api/recommended-actions/{action_id}/dismiss', {
      params: { path: { action_id } },
    }),
  resolve: (action_id: number) =>
    api.POST('/api/recommended-actions/{action_id}/resolve', {
      params: { path: { action_id } },
    }),
};

export const syncHistoricalApi = {
  start: (body: Schemas['SyncSessionCreateRequest']) => api.POST('/api/sync/historical', { body }),
  list: (q: { limit?: number; status?: string } = {}) =>
    api.GET('/api/sync/historical', { params: { query: q } }),
  get: (sessionUuid: string) =>
    api.GET('/api/sync/historical/{session_uuid}', {
      params: { path: { session_uuid: sessionUuid } },
    }),
  cancel: (sessionUuid: string) =>
    api.POST('/api/sync/historical/{session_uuid}/cancel', {
      params: { path: { session_uuid: sessionUuid } },
    }),
  /**
   * Returns the SSE URL using the same host resolution as the REST client
   * (apiUrl). Use with `new EventSource(url, { withCredentials: true })`
   * — the stream sends 'state', 'ping', and 'done' events. EventSource
   * doesn't accept headers, so SSE relies on the httpOnly auth cookie
   * that the rest of the API already uses (CORS sends `credentials: include`).
   */
  streamUrl: (sessionUuid: string): string =>
    apiUrl(`/api/sync/historical/${encodeURIComponent(sessionUuid)}/stream`),
};
